import React, { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { InlineEdit } from 'uiComponents/input'
import { BaseRouteParams } from 'hocs'
import { addMessage, removeMessage } from 'uiComponents/messages/actions'
import { useEditDiscountPoolMutation } from 'discounts/reduxQuery'
import { useParams } from 'react-router-dom'
import { getHashParam, getHashRoute, removeHash } from 'utils/hash'
import { DiscountPool } from 'discounts/types'

interface InlineNameEditProps {
    discountPool: DiscountPool
}

export const InlineNameEdit: React.FC<InlineNameEditProps> = ({ discountPool }) => {
    const dispatch = useDispatch()
    const { accountSlug } = useParams<BaseRouteParams>()
    const [updateName] = useEditDiscountPoolMutation()
    const inlineEditRef = useRef<InlineEdit>(null)
    const editingPoolUuid = getHashRoute() === 'edit_pool_name' ? getHashParam('poolUuid') : null
    const isEditMode = editingPoolUuid === discountPool.uuid

    useEffect(() => {
        if (isEditMode && inlineEditRef.current) {
            setTimeout(() => {
                if (inlineEditRef.current) {
                    inlineEditRef.current.enableEdit({ stopPropagation: () => {} } as React.MouseEvent)
                }
            }, 0)
        }
    }, [isEditMode])

    const handleEditAccept = useCallback(
        async (name: string) => {
            const isNameValid = name.length
            const errorShowTime = 5000

            removeHash()

            if (!isNameValid) {
                dispatch(addMessage('inline-name', 'error', 'Name cannot be empty.'))
            } else {
                try {
                    await updateName({ uuid: discountPool.uuid, name, accountSlug })
                } catch {
                    dispatch(
                        addMessage(
                            'inline-name',
                            'error',
                            `Oops! There was a problem with saving name ${name}. Please try again.`,
                        ),
                    )
                }
            }

            setTimeout(() => {
                dispatch(removeMessage('inline-name'))
            }, errorShowTime)
        },
        [discountPool.uuid],
    )

    return (
        <InlineEdit
            ref={inlineEditRef}
            responsiveWidth
            value={discountPool.name}
            onEditAccept={handleEditAccept}
            accountSlug={accountSlug}
            onCancel={removeHash}
            showEditIcon={false}
        />
    )
}
